import { useMemo } from 'react';
import { CanvasserType } from '@signalvote/shared';
import type { CanvasserStatsItem } from '@signalvote/shared';

export interface GroupStats {
  count: number;
  avgDoorsPerHour: number | null;
  avgReachRate: number | null;
  avgSurveyCompletion: number | null;
  avgReliability: number | null;
  totalHours: number;
}

export interface PaidVsVolunteerComparison {
  volunteer: GroupStats;
  paid: GroupStats;
  directHire: GroupStats | null;
  vendorManaged: GroupStats | null;
}

function computeGroupStats(items: CanvasserStatsItem[]): GroupStats {
  const count = items.length;
  const totalHours = items.reduce((sum, c) => sum + c.hoursWorked, 0);

  // Doors per hour: compute per-canvasser d/h, then average (only those with >0 hours)
  const doorsPerHourValues = items
    .filter((c) => c.hoursWorked > 0)
    .map((c) => c.doorsKnocked / c.hoursWorked);
  const avgDoorsPerHour =
    doorsPerHourValues.length > 0
      ? doorsPerHourValues.reduce((a, b) => a + b, 0) / doorsPerHourValues.length
      : null;

  // Average of non-null metric values
  const reachRates = items.map((c) => c.reachRate).filter((v): v is number => v !== null);
  const avgReachRate = reachRates.length > 0 ? reachRates.reduce((a, b) => a + b, 0) / reachRates.length : null;

  const surveyRates = items.map((c) => c.surveyCompletionRate).filter((v): v is number => v !== null);
  const avgSurveyCompletion = surveyRates.length > 0 ? surveyRates.reduce((a, b) => a + b, 0) / surveyRates.length : null;

  const reliabilities = items.map((c) => c.reliabilityRate).filter((v): v is number => v !== null);
  const avgReliability = reliabilities.length > 0 ? reliabilities.reduce((a, b) => a + b, 0) / reliabilities.length : null;

  return {
    count,
    avgDoorsPerHour,
    avgReachRate,
    avgSurveyCompletion,
    avgReliability,
    totalHours,
  };
}

export function hasBothCanvasserTypes(items: CanvasserStatsItem[]): boolean {
  let hasVolunteer = false;
  let hasPaid = false;
  for (const item of items) {
    if (item.canvasserType === CanvasserType.Volunteer) hasVolunteer = true;
    if (item.canvasserType === CanvasserType.Paid) hasPaid = true;
    if (hasVolunteer && hasPaid) return true;
  }
  return false;
}

export function computePaidVsVolunteerComparison(
  items: CanvasserStatsItem[]
): PaidVsVolunteerComparison | null {
  const volunteers = items.filter((c) => c.canvasserType === CanvasserType.Volunteer);
  const paid = items.filter((c) => c.canvasserType === CanvasserType.Paid);

  // Hide when only one type exists
  if (volunteers.length === 0 || paid.length === 0) return null;

  const directHireItems = paid.filter((c) => c.vendorId === null);
  const vendorManagedItems = paid.filter((c) => c.vendorId !== null);

  // Only show sub-groups when both exist
  const hasSubGroups = directHireItems.length > 0 && vendorManagedItems.length > 0;

  return {
    volunteer: computeGroupStats(volunteers),
    paid: computeGroupStats(paid),
    directHire: hasSubGroups ? computeGroupStats(directHireItems) : null,
    vendorManaged: hasSubGroups ? computeGroupStats(vendorManagedItems) : null,
  };
}

export function usePaidVsVolunteerComparison(items: CanvasserStatsItem[]) {
  return useMemo(() => computePaidVsVolunteerComparison(items), [items]);
}
